/**
 * Onboarding Welcome Component - "Votre riad est prêt"
 * 
 * Affiché après la fin du Setup Wizard avec:
 * - Message de bienvenue personnalisé
 * - Actions rapides pour démarrer:
 *   - Créer première réservation
 *   - Voir calendrier
 *   - Ajouter équipe
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, PartyPopper, Calendar, Users, PlusCircle,
  ArrowRight, Sparkles, Building2, ClipboardList, Receipt,
  ChevronRight, Play, X
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import Confetti from 'react-confetti';

const OnboardingWelcome = ({ riadName, onDismiss }) => {
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(true);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', handleResize);
    
    // Stop confetti after 5 seconds
    const timer = setTimeout(() => setShowConfetti(false), 5000);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const quickActions = [
    {
      id: 'reservation',
      icon: PlusCircle,
      title: 'Créer ma première réservation',
      description: 'Enregistrez votre premier client',
      color: 'from-green-500 to-emerald-600',
      bgLight: 'bg-green-50',
      textColor: 'text-green-600',
      action: () => navigate('/reservations?action=new'),
      primary: true
    },
    {
      id: 'calendar',
      icon: Calendar,
      title: 'Voir mon calendrier',
      description: 'Visualisez vos disponibilités',
      color: 'from-blue-500 to-cyan-600',
      bgLight: 'bg-blue-50',
      textColor: 'text-blue-600',
      action: () => navigate('/calendar')
    },
    {
      id: 'team',
      icon: Users,
      title: 'Ajouter mon équipe',
      description: 'Invitez vos collaborateurs',
      color: 'from-violet-500 to-purple-600',
      bgLight: 'bg-violet-50',
      textColor: 'text-violet-600',
      action: () => navigate('/users')
    }
  ];

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 z-50 overflow-auto">
      {showConfetti && (
        <Confetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={false}
          numberOfPieces={200}
          colors={['#F59E0B', '#EF4444', '#8B5CF6', '#10B981', '#3B82F6']}
        />
      )}
      
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-2xl w-full">
          {/* Close button */}
          <div className="flex justify-end mb-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onDismiss}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Success header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg shadow-green-200 mb-6">
              <CheckCircle2 className="w-10 h-10 text-white" />
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-3">
              <PartyPopper className="w-6 h-6 text-amber-500" />
              <h1 className="text-3xl font-bold text-gray-900">
                Votre riad est prêt !
              </h1>
              <PartyPopper className="w-6 h-6 text-amber-500 transform scale-x-[-1]" />
            </div>
            
            <p className="text-lg text-gray-600 max-w-md mx-auto">
              <span className="font-semibold text-amber-600">{riadName || 'Votre établissement'}</span> est maintenant configuré. 
              Commencez à gérer vos réservations dès maintenant !
            </p>
          </div>

          {/* Quick actions */}
          <div className="space-y-4 mb-8">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider text-center">
              Par où commencer ?
            </h2>
            
            <div className="grid gap-4">
              {quickActions.map((action) => (
                <Card 
                  key={action.id}
                  className={`cursor-pointer transition-all hover:shadow-lg hover:-translate-y-0.5 ${
                    action.primary ? 'ring-2 ring-green-500 ring-offset-2' : ''
                  }`}
                  onClick={action.action}
                  data-testid={`welcome-action-${action.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className={`w-14 h-14 rounded-xl ${action.bgLight} flex items-center justify-center shrink-0`}>
                        <action.icon className={`w-7 h-7 ${action.textColor}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{action.title}</h3>
                          {action.primary && (
                            <Badge className="bg-green-100 text-green-700 text-xs">Recommandé</Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{action.description}</p>
                      </div>
                      <ChevronRight className={`w-5 h-5 ${action.textColor}`} />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Skip link */}
          <div className="text-center">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-gray-500 hover:text-gray-700"
            >
              Aller au tableau de bord
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWelcome;
